/**
 * Email + password sign-in — pure rules and wording (13 Oct 2026).
 *
 * Why it exists: Google sign-in is the one path many people don't want or
 * can't use (no Google account, a work device, Google blocking a new device),
 * and Sign in with Apple exists only on iPhone. Email + password works the
 * same in every shell — web, Android WebView, iOS WKWebView — through the
 * Firebase JS SDK, with no browser hop (the magic link cannot complete inside
 * the native shell, which is why it is web-only).
 *
 * Firebase behaviour this wording relies on (Google Cloud, "Enable or disable
 * email enumeration protection", fetched 13 Oct 2026; on by default for
 * projects created on/after 15 Sept 2023):
 *  - a wrong password and an unknown email return the SAME error
 *    (INVALID_LOGIN_CREDENTIALS → auth/invalid-credential), so the message
 *    must never say which one it was;
 *  - sign-up still reports EMAIL_EXISTS (auth/email-already-in-use);
 *  - a password reset for an unknown address returns no error, so the
 *    confirmation must be conditional ("if an account exists…").
 *
 * Account policy: Firebase is on one account per email. Creating an account
 * is an explicit button and starts EMPTY (syncCore `start-empty`), so this
 * path can never seed a board into someone else's account.
 */

export type EmailAuthMode = 'sign-in' | 'create';

/** Firebase's own minimum is 6; ask for 8 when CREATING an account. */
export const MIN_NEW_PASSWORD = 8;

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;

/** Trimmed, lower-cased address — what we send to Firebase. */
export function normaliseEmail(raw: string): string {
  return raw.trim().toLowerCase();
}

/** A problem to show before any network call, or null when the form can submit. */
export function validateEmailPassword(
  email: string,
  password: string,
  mode: EmailAuthMode,
): string | null {
  const e = normaliseEmail(email);
  if (!e) return 'Enter your email address.';
  if (!EMAIL_RE.test(e)) return 'That doesn’t look like an email address — check for typos.';
  if (!password) return 'Enter your password.';
  if (mode === 'create' && password.length < MIN_NEW_PASSWORD) {
    return `Choose a password of at least ${MIN_NEW_PASSWORD} characters.`;
  }
  return null;
}

/** Only the address is needed to request a reset link. */
export function validateResetEmail(email: string): string | null {
  const e = normaliseEmail(email);
  if (!e) return 'Enter your email address first, then tap “Forgot password?”.';
  if (!EMAIL_RE.test(e)) return 'That doesn’t look like an email address — check for typos.';
  return null;
}

function codeOf(err: unknown): string {
  if (err && typeof err === 'object' && 'code' in err) {
    const code = (err as { code?: unknown }).code;
    if (typeof code === 'string') return code;
  }
  return '';
}

/**
 * Plain-language message for a failed email sign-in / sign-up. Unknown
 * errors keep their raw text (CLAUDE.md §2 — the next failure must diagnose
 * itself on screen).
 */
export function describeEmailAuthError(err: unknown, mode: EmailAuthMode): string {
  const code = codeOf(err);
  switch (code) {
    case 'auth/invalid-credential':
    case 'auth/invalid-login-credentials':
    case 'auth/wrong-password':
    case 'auth/user-not-found':
      return (
        'That email and password don’t match a ZoneIn account. Check both, or tap “Forgot password?”. ' +
        'If you usually sign in with Google or Apple, use that button instead.'
      );
    case 'auth/email-already-in-use':
      return (
        'That email already has a ZoneIn account — tap “Sign in” instead. If you created it with ' +
        'Google or Apple, sign in with that button so your tasks and any lifetime access come with you.'
      );
    case 'auth/weak-password':
      return `Choose a stronger password — at least ${MIN_NEW_PASSWORD} characters.`;
    case 'auth/invalid-email':
      return 'That doesn’t look like an email address — check for typos.';
    case 'auth/missing-password':
      return 'Enter your password.';
    case 'auth/too-many-requests':
      return 'Too many attempts — wait a few minutes, or tap “Forgot password?” to reset it.';
    case 'auth/network-request-failed':
      return 'No connection — check your internet and try again.';
    case 'auth/user-disabled':
      return 'This account has been disabled. Contact support through the User Guide for help.';
    case 'auth/operation-not-allowed':
      return 'Email sign-in is switched off for ZoneIn right now. Please use Google or Apple sign-in.';
    default: {
      const raw = err instanceof Error ? `${err.name}: ${err.message}` : String(err);
      return `${mode === 'create' ? 'Creating the account' : 'Signing in'} failed — ${raw}`;
    }
  }
}

/** Shown after a reset request whatever happened server-side (enumeration-safe). */
export function resetSentMessage(email: string): string {
  return (
    `If ${normaliseEmail(email)} has a ZoneIn account, a password-reset link is on its way. ` +
    'Check spam if it hasn’t arrived in a minute.'
  );
}
